import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { PaginationState } from '../TanstackTable/types';
import { DEFAULT_INITIAL_PAGE_SIZE } from '../TanstackTable/constants';
import { useAutoPageSize, UseAutoPageSizeOptions } from './useAutoPageSize';
import {
  nextPagination,
  PaginatedTableProps,
  PaginationResetOptions,
  useResetOnChange,
} from './pagination';

export interface UseServerPaginationOptions extends UseAutoPageSizeOptions, PaginationResetOptions {
  /**
   * Page size used before the first measurement arrives, and the fixed size
   * when auto-fit is `disabled`.
   */
  initialPageSize?: number;
  /**
   * How long a newly measured size must hold before it is committed. Dragging a
   * window edge sweeps through many sizes; without this each one is a request.
   * Default `250`.
   */
  settleMs?: number;
}

export interface UseServerPaginationResult {
  /** 0-based page index to send to the API. */
  page: number;
  /** Page size to send to the API. */
  size: number;
  /**
   * `false` until the page size has settled. Gate the query on it (react-query
   * `enabled`) so the first request goes out at the right size instead of
   * firing at a placeholder and immediately refetching.
   */
  isReady: boolean;
  /** Jump back to the first page. Wired to `resetOn`, and callable directly. */
  resetPage: () => void;
  /** Spread onto a server-mode `<TanstackTable>`. */
  tableProps: PaginatedTableProps;
}

/** Default step for server mode: each distinct size is a round trip. */
const DEFAULT_SERVER_STEP = 5;

/**
 * Server-side pagination for `TanstackTable`, including auto-fit.
 *
 * Owns the pagination state so a page does not have to, and folds in the four
 * things auto-fitting a *server* table needs: the fitted size is quantised, the
 * first request waits for the measurement, resize storms settle before
 * refetching, and the top visible row is held in place when the size changes.
 *
 * ```tsx
 * const { page, size, isReady, tableProps } = useServerPagination();
 * const { data, isFetching } = useDevicesData(companyId, page, size, {
 *   enabled: isReady,
 *   keepPreviousData: true,
 * });
 *
 * <TanstackTable
 *   mode="server"
 *   data={data?.content ?? []}
 *   rowCount={data?.totalElements ?? 0}
 *   columns={columns}
 *   isLoading={isFetching}
 *   {...tableProps}
 * />
 * ```
 */
export function useServerPagination({
  initialPageSize = DEFAULT_INITIAL_PAGE_SIZE,
  settleMs = 250,
  step = DEFAULT_SERVER_STEP,
  resetOn,
  ...autoPageSizeOptions
}: UseServerPaginationOptions = {}): UseServerPaginationResult {
  const autoPageSize = useAutoPageSize({ ...autoPageSizeOptions, step });
  const isAutoPageSizeOn = !autoPageSizeOptions.disabled;

  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: initialPageSize,
  });
  const [isReady, setIsReady] = useState(!isAutoPageSizeOn);

  const paginationRef = useRef(pagination);
  paginationRef.current = pagination;

  // `null` while the user has pinned a size, so a pending commit cannot
  // overwrite their choice.
  const fittedPageSize = autoPageSize.pageSize;

  // Hold a freshly measured size until it stops changing, then commit it. The
  // very first one is committed immediately: nothing has been fetched yet, so
  // there is no request to save and the wait would only delay first paint.
  useEffect(() => {
    if (!isAutoPageSizeOn || fittedPageSize === null) return;
    if (fittedPageSize === paginationRef.current.pageSize) {
      if (!isReady) setIsReady(true);
      return;
    }

    const commit = (): void => {
      setPagination((previous: PaginationState) => {
        // Keep the row currently at the top of the page at the top of the new
        // one, so resizing browses the data instead of jumping through it.
        const anchorRow = previous.pageIndex * previous.pageSize;
        return {
          pageIndex: Math.floor(anchorRow / fittedPageSize),
          pageSize: fittedPageSize,
        };
      });
      setIsReady(true);
    };

    if (!isReady) {
      commit();
      return;
    }

    const timer = setTimeout(commit, settleMs);
    return (): void => clearTimeout(timer);
  }, [isAutoPageSizeOn, fittedPageSize, settleMs, isReady]);

  const resetPage = useCallback((): void => {
    setPagination((previous: PaginationState) =>
      previous.pageIndex === 0 ? previous : { ...previous, pageIndex: 0 }
    );
  }, []);

  const onPaginationChange = useCallback((next: PaginationState): void => {
    setPagination((previous: PaginationState) => nextPagination(previous, next));
  }, []);

  useResetOnChange(resetOn, resetPage);

  // Memoised so the table is not handed a fresh props object on every render of
  // the page — which would also waste the memo `useAutoPageSize` already does.
  const tableProps = useMemo<PaginatedTableProps>(
    () => ({ ...autoPageSize.tableProps, pagination, onPaginationChange }),
    [autoPageSize.tableProps, pagination, onPaginationChange]
  );

  return {
    page: pagination.pageIndex,
    size: pagination.pageSize,
    isReady,
    resetPage,
    tableProps,
  };
}

export default useServerPagination;
